import { useState, useEffect } from "react";
import { Reorder, useDragControls } from "framer-motion";
import { toast } from "sonner";
import { Music4, Film, Clapperboard, Trash2, Plus, FolderOpen, RefreshCw, Share2, Download, Sparkles, ImageIcon, Loader2, Smartphone, Shuffle, Timer, GripVertical, ListOrdered, Waves, Instagram, MessageCircle, Music2, AlertTriangle, Receipt, ShieldCheck, SlidersHorizontal, Volume2, VolumeX, Mic2, RotateCcw } from "lucide-react";
import api, { mediaUrl, downloadUrl, API_BASE } from "@/lib/api";
import { Scissors, FileText, Copy, Pencil, UserCircle2, Coins, Globe, Wrench } from "lucide-react";
import { fmtCredits, filmSceneCredits } from "@/lib/cost";
import { TrailerDialog, TrailerResult } from "@/components/TrailerDialog";
import StatusBadge from "@/components/StatusBadge";
import ShareDialog from "@/components/ShareDialog";
import VideoEditor from "@/components/VideoEditor";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const MV_TRANSITIONS = [
  { id: "dissolve", label: "Disolvencia (suave)" },
  { id: "fade", label: "Fundido" },
  { id: "wipe", label: "Barrido" },
  { id: "slide", label: "Deslizamiento" },
  { id: "cut", label: "Corte directo" },
];

const TYPE = {
  song: { icon: Music4, label: "Canción" },
  video: { icon: Film, label: "Video" },
  shortfilm: { icon: Clapperboard, label: "Cortometraje" },
};

const EXTEND_OPTS = [
  { s: 120, label: "2 min" },
  { s: 180, label: "3 min" },
  { s: 240, label: "4 min" },
];

function QueueRow({ item }) {
  const controls = useDragControls();
  const T = TYPE[item.type] || TYPE.video;
  const Icon = T.icon;
  return (
    <Reorder.Item value={item} dragListener={false} dragControls={controls}
      className="flex items-center gap-3 bg-[#0B0C13] border border-white/10 rounded-xl px-3 py-2.5 select-none"
      data-testid={`queue-item-${item.pid}`}>
      <button onPointerDown={(e) => controls.start(e)} className="cursor-grab active:cursor-grabbing text-[#64748B] hover:text-white touch-none"
        data-testid={`queue-drag-${item.pid}`}>
        <GripVertical className="w-4 h-4" />
      </button>
      <div className="w-8 h-8 rounded-lg bg-[#FF334B]/12 flex items-center justify-center shrink-0"><Icon className="w-4 h-4 text-[#FF334B]" /></div>
      <span className="font-head text-sm truncate flex-1">{item.title}</span>
      <span className="mono-label">{T.label}</span>
    </Reorder.Item>
  );
}

export default function ProjectsPanel({ projects, characters = [], reload, goTo, loading, loadError }) {
  const charMap = Object.fromEntries((characters || []).map((c) => [c.id, c]));
  const [busy, setBusy] = useState({});
  const [mvTransition, setMvTransition] = useState({});   // pid -> transition id
  const [mvConfirm, setMvConfirm] = useState(null);       // { p, full, scenes, cost }
  const [pubCat, setPubCat] = useState({});               // pid -> category for publishing
  const [mixGains, setMixGains] = useState({});
  const [mixOffsets, setMixOffsets] = useState({});
  const [loudness, setLoudness] = useState({});   // pid -> -14 | -9
  const [presets, setPresets] = useState([]);
  const STEM_LABEL = { vocals: "Voz", drums: "Batería", bass: "Bajo", other: "Otros" };
  const BUILTIN_PRESETS = [
    { name: "Original", gains: { vocals: 100, drums: 100, bass: 100, other: 100 } },
    { name: "Solo voz", gains: { vocals: 100, drums: 0, bass: 0, other: 0 } },
    { name: "Karaoke (sin voz)", gains: { vocals: 0, drums: 100, bass: 100, other: 100 } },
    { name: "Instrumental", gains: { vocals: 0, drums: 100, bass: 100, other: 70 } },
  ];
  const gainOf = (pid, name) => { const g = mixGains[pid]?.[name]; return g === undefined ? 100 : g; };
  const setGain = (pid, name, val) => setMixGains((m) => ({ ...m, [pid]: { ...(m[pid] || {}), [name]: val } }));
  const offsetOf = (pid, name) => { const o = mixOffsets[pid]?.[name]; return o === undefined ? 0 : o; };
  const setOffset = (pid, name, val) => setMixOffsets((m) => ({ ...m, [pid]: { ...(m[pid] || {}), [name]: val } }));
  const applyPreset = (pid, gains) => setMixGains((m) => ({ ...m, [pid]: { ...(m[pid] || {}), ...gains } }));
  const savePreset = (pid) => {
    const name = window.prompt("Nombre del preajuste (ej. Mi karaoke)");
    if (!name?.trim()) return;
    const gains = { vocals: gainOf(pid, "vocals"), drums: gainOf(pid, "drums"), bass: gainOf(pid, "bass"), other: gainOf(pid, "other") };
    const next = [...presets.filter((p) => p.name !== name.trim()), { name: name.trim(), gains }];
    setPresets(next);
    localStorage.setItem("mfp_mix_presets", JSON.stringify(next));
    toast.success("Preajuste guardado");
  };
  const deletePreset = (name) => {
    const next = presets.filter((p) => p.name !== name);
    setPresets(next);
    localStorage.setItem("mfp_mix_presets", JSON.stringify(next));
  };
  useEffect(() => {
    try { setPresets(JSON.parse(localStorage.getItem("mfp_mix_presets") || "[]")); } catch { /* noop */ }
  }, []);
  const [chooser, setChooser] = useState(null);
  const [coverOpts, setCoverOpts] = useState(null);
  const [share, setShare] = useState({ open: false, project: null });
  const [queue, setQueue] = useState([]);
  const [extendOpen, setExtendOpen] = useState(null);
  const [animOpen, setAnimOpen] = useState(null);
  const [editor, setEditor] = useState(null);
  const [lyricsOpen, setLyricsOpen] = useState(null);
  const [scenesOpen, setScenesOpen] = useState(null);
  const [receipt, setReceipt] = useState({});   // pid -> entries[] | 'loading'
  const [retryInfo, setRetryInfo] = useState(null);
  const [trailerFor, setTrailerFor] = useState(null);   // { id, title, cost } — vista previa de costo antes de reintentar
  const [retrying, setRetrying] = useState(false);
  const readySongs = projects.filter((p) => p.type === "song" && p.status === "ready" && p.result?.audio_path);

  const loadQueue = async () => {
    try { const r = await api.get("/queue"); setQueue(r.data); } catch {}
  };
  useEffect(() => { loadQueue(); }, [projects]); // refresh queue whenever projects poll updates

  const reorder = async (newOrder) => {
    setQueue(newOrder);
    try { await api.post("/queue/reorder", { order: newOrder.map((i) => i.pid) }); }
    catch { toast.error("No se pudo reordenar la cola"); loadQueue(); }
  };

  const toggleReceipt = async (p) => {
    if (receipt[p.id]) { setReceipt((r) => { const n = { ...r }; delete n[p.id]; return n; }); return; }
    setReceipt((r) => ({ ...r, [p.id]: "loading" }));
    try {
      const res = await api.get(`/projects/${p.id}/receipt`);
      setReceipt((r) => ({ ...r, [p.id]: res.data }));
    } catch { setReceipt((r) => ({ ...r, [p.id]: { error: true, credits: [], fal: null } })); }
  };

  const setB = (id, v) => setBusy((b) => ({ ...b, [id]: v }));

  const retryAll = async () => {
    try { const r = await api.post(`/projects/retry-failed`); await reload(); toast.success(`Reintentando ${r.data.requeued} proyecto(s)`); }
    catch { toast.error("No se pudo reintentar"); }
  };

  const selectVariant = async (p, i) => {
    try { await api.post(`/projects/${p.id}/variant/select`, { index: i }); await reload(); toast.success("Versión elegida"); }
    catch { toast.error("No se pudo elegir"); }
  };

  const separateStems = async (p) => {
    setB(p.id, "stems");
    try { await api.post(`/projects/${p.id}/stems`); await reload(); toast.success("Separando pistas (voz, batería, bajo, otros)…"); }
    catch (e) { toast.error(e?.response?.data?.detail || "No se pudieron separar las pistas"); }
    finally { setB(p.id, null); }
  };

  const runMix = async (p) => {
    setB(p.id, "mix");
    try {
      const g = {};
      ["vocals", "drums", "bass", "other"].forEach((n) => { g[n] = gainOf(p.id, n) / 100; });
      await api.post(`/projects/${p.id}/mix`, { gains: g });
      await reload();
      toast.success("Exportando tu mezcla…");
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo exportar la mezcla"); }
    finally { setB(p.id, null); }
  };

  const runMaster = async (p) => {
    setB(p.id, "master");
    try {
      const tracks = ["vocals", "drums", "bass", "other"]
        .filter((n) => p.result?.stems?.[n])
        .map((n) => ({ stem: n, gain: gainOf(p.id, n) / 100, offset_sec: offsetOf(p.id, n) }));
      await api.post(`/projects/${p.id}/master`, {
        tracks,
        include_extra: !!p.result?.timeline_extra,
        extra_gain: gainOf(p.id, "extra") / 100,
        extra_offset_sec: offsetOf(p.id, "extra"),
        loudness: loudness[p.id] ?? -14,
      });
      await reload();
      toast.success("Generando tu máster…");
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo generar el máster"); }
    finally { setB(p.id, null); }
  };

  const uploadExtra = async (p, file) => {
    if (!file) return;
    setB(p.id, "upload");
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/projects/${p.id}/master/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      await reload();
      toast.success("Pista extra añadida");
    } catch { toast.error("No se pudo subir la pista"); }
    finally { setB(p.id, null); }
  };

  const promoteMaster = async (p) => {
    try {
      await api.post(`/projects/${p.id}/master/promote`);
      await reload();
      toast.success("El máster es ahora la versión principal");
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo fijar el máster"); }
  };

  const askMusicVideo = (p, full = false) => {
    const dur = Number(p?.meta?.duration || p?.result?.duration || 60) || 60;
    const maxScenes = Math.max(2, Math.ceil(dur / 6));
    const scenes = full ? maxScenes : Math.min(6, maxScenes);
    setMvConfirm({ p, full, scenes, cost: scenes * 7 });
  };

  const runMusicVideo = async (p, full = false) => {
    setMvConfirm(null);
    setB(p.id, "musicvideo");
    try {
      const r = await api.post("/musicvideo", {
        song_project_id: p.id, quality: "pro", full,
        transition: mvTransition[p.id] || "dissolve",
      });
      await reload();
      toast.success(`Creando videoclip (${r.data.scenes} escenas) con tu personaje…`);
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo crear el videoclip"); }
    finally { setB(p.id, null); }
  };

  const copyLyrics = async (p) => {
    const txt = p.result?.lyrics || "";
    if (!txt.trim()) { toast.error("Esta canción no tiene letra guardada"); return; }
    try { await navigator.clipboard.writeText(txt); toast.success("Letra copiada al portapapeles"); }
    catch { toast.error("No se pudo copiar la letra"); }
  };

  const reeditSong = (p) => {
    localStorage.setItem("mfp_reedit", JSON.stringify({
      title: p.title || "",
      lyrics: p.result?.lyrics || "",
      genre: p.meta?.genre || "",
      lockLyrics: true,
    }));
    goTo("cancion");
    toast.success("Abriendo la letra para editar y volver a generar…");
  };

  const regenScene = async (p, i) => {
    setB(p.id, `regen-${i}`);
    try {
      await api.post(`/projects/${p.id}/scenes/${i}/regenerate`);
      await reload();
      toast.success(`Regenerando escena ${i + 1} (solo esa)…`);
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo regenerar la escena"); }
    finally { setB(p.id, null); }
  };
  const runKaraoke = async (p) => {
    setB(p.id, "karaoke");
    try {
      await api.post(`/projects/${p.id}/karaoke`);
      await reload();
      toast.success("Generando tu karaoke…");
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo crear el karaoke"); }
    finally { setB(p.id, null); }
  };

  const remix = async (p) => {
    setB(p.id, "remix");
    try { await api.post(`/projects/${p.id}/remix`); await reload(); toast.success("Remezclando una nueva versión…"); }
    catch (e) { toast.error(e?.response?.data?.detail || "No se pudo remezclar"); }
    finally { setB(p.id, null); }
  };

  const extend = async (p, seconds) => {
    setExtendOpen(null);
    setB(p.id, "extend");
    try { await api.post(`/projects/${p.id}/extend`, { duration: seconds }); await reload(); toast.success("Extendiendo tu canción…"); }
    catch (e) { toast.error(e?.response?.data?.detail || "No se pudo extender"); }
    finally { setB(p.id, null); }
  };

  const animateCover = async (p, format) => {
    setAnimOpen(null);
    setB(p.id, "animcover");
    try { await api.post(`/projects/${p.id}/animate-cover`, { format }); await reload(); toast.success("Creando tu portada animada…"); }
    catch (e) { toast.error(e?.response?.data?.detail || "No se pudo crear la portada animada"); }
    finally { setB(p.id, null); }
  };

  const shareTo = async (p, path, target) => {
    const url = mediaUrl(path);
    const text = `${p.title} · Metáfora Play`;
    if (target === "whatsapp") {
      window.open(`https://wa.me/?text=${encodeURIComponent(text + " " + url)}`, "_blank");
      return;
    }
    try {
      const res = await fetch(url);
      const blob = await res.blob();
      const file = new File([blob], `${p.title}-portada.mp4`, { type: "video/mp4" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: p.title, text });
        return;
      }
    } catch { /* fall through to desktop fallback */ }
    try { await navigator.clipboard.writeText(url); } catch { /* noop */ }
    toast.info(target === "tiktok" ? "Descarga el video y súbelo en TikTok (enlace copiado)" : "Descarga el video y súbelo en Instagram (enlace copiado)");
    window.open(target === "tiktok" ? "https://www.tiktok.com/upload" : "https://www.instagram.com", "_blank");
  };

  const del = async (p) => {
    const willRefund = p.status !== "ready" && (p.billing?.net || 0) > 0;
    const msg = willRefund
      ? `¿Borrar "${p.title}"? Este proyecto tiene un cargo sin entregar; al borrarlo se te reembolsará automáticamente.`
      : `¿Borrar "${p.title}"? Esta acción no se puede deshacer.`;
    if (!window.confirm(msg)) return;
    try {
      const r = await api.delete(`/projects/${p.id}`);
      await reload();
      const ref = r?.data?.refunded || 0;
      toast.success(ref > 0 ? `Proyecto eliminado · se reembolsaron ${Math.round(ref)} créditos` : "Proyecto eliminado");
    } catch { toast.error("No se pudo borrar el proyecto"); }
  };

  const retry = async (p) => {
    try {
      const r = await api.get(`/projects/${p.id}/retry_cost`);
      setRetryInfo({ id: p.id, title: p.title, cost: r.data });
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo calcular el costo del reintento"); }
  };

  const confirmRetry = async () => {
    if (!retryInfo) return;
    setRetrying(true);
    try {
      await api.post(`/projects/${retryInfo.id}/retry?confirm=true`);
      setRetryInfo(null);
      await reload();
      toast.success("Reintentando la generación…");
    } catch (e) {
      const d = e?.response?.data?.detail || "No se pudo reintentar";
      if (e?.response?.status === 402) toast.error(d, { action: { label: "Recargar", onClick: () => window.location.assign("/precios") } });
      else toast.error(d);
    }
    finally { setRetrying(false); }
  };

  const assembleOnly = async () => {
    if (!retryInfo) return;
    setRetrying(true);
    try {
      await api.post(`/projects/${retryInfo.id}/assemble`);
      setRetryInfo(null);
      await reload();
      toast.success("Uniendo las escenas listas (gratis)…");
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo unir el video"); }
    finally { setRetrying(false); }
  };

  const cancelJob = async (id) => {
    try { await api.post(`/projects/${id}/cancel`); await reload(); toast.success("Cancelado y crédito reembolsado"); }
    catch (e) { toast.error(e?.response?.data?.detail || "No se pudo cancelar"); }
  };

  const openShare = (p) => setShare({ open: true, project: { id: p.id, title: p.title } });

  const publishProject = async (p) => {
    setB(p.id, "publish");
    try {
      const category = pubCat[p.id] || (p.type === "song" ? "cancion" : "video");
      await api.post(`/projects/${p.id}/publish`, { category });
      await reload();
      toast.success("¡Publicado en la Comunidad! 🌍");
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo publicar"); }
    finally { setB(p.id, null); }
  };

  const unpublishProject = async (p) => {
    setB(p.id, "publish");
    try {
      await api.post(`/projects/${p.id}/unpublish`);
      await reload();
      toast.success("Quitado de la Comunidad");
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo quitar"); }
    finally { setB(p.id, null); }
  };

  const openCertificate = (p) => window.open(`${API_BASE}/projects/${p.id}/certificate`, "_blank");

  const doDownload = async (p, aspect, suffix) => {
    setB(p.id, aspect === "9:16" ? "dl916" : "dl");
    try {
      const r = await api.post(`/projects/${p.id}/export`, { aspect, quality: "Full HD 1080p" });
      const ext = r.data.kind === "audio" ? ".wav" : ".mp4";
      const a = document.createElement("a");
      a.href = downloadUrl(r.data.download_path, p.title + suffix + ext);
      a.download = p.title + suffix + ext;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success("Descarga lista");
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo descargar"); }
    finally { setB(p.id, null); }
  };

  const aiCover = async (p) => {
    setB(p.id, "cover");
    try {
      const r = await api.post(`/projects/${p.id}/cover/options`);
      setCoverOpts({ id: p.id, options: r.data.options });
      await reload();
      toast.success("Elige tu portada favorita");
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo generar la portada"); }
    finally { setB(p.id, null); }
  };

  const selectCover = async (p, path) => {
    try { await api.post(`/projects/${p.id}/cover/select`, { poster_path: path }); await reload(); setCoverOpts(null); toast.success("Portada actualizada"); }
    catch (e) { toast.error(e?.response?.data?.detail || "No se pudo actualizar"); }
  };

  const pickScene = async (p, idx) => {
    setB(p.id, "poster");
    try { await api.post(`/projects/${p.id}/poster`, { scene_index: idx }); await reload(); setChooser(null); toast.success("Portada actualizada"); }
    catch (e) { toast.error(e?.response?.data?.detail || "No se pudo actualizar"); }
    finally { setB(p.id, null); }
  };

  const preview = (p) => {
    if (p.status !== "ready") return null;
    if (p.type === "song" && p.result?.audio_path) {
      const covers = [];
      if (p.result?.animated_cover_path) covers.push({ path: p.result.animated_cover_path, label: "9:16 Stories", ar: "9/16" });
      if (p.result?.animated_cover_square_path) covers.push({ path: p.result.animated_cover_square_path, label: "1:1 Feed", ar: "1/1" });
      return (
        <>
          <audio src={mediaUrl(p.result.audio_path)} controls className="w-full mt-3" />
          <div className="mt-3" data-testid={`stems-block-${p.id}`}>
            {p.result?.stems ? (
              <div className="rounded-xl border border-white/8 bg-[#0B0C13] p-3">
                <div className="mono-label mb-2 inline-flex items-center gap-1.5"><Music2 className="w-3.5 h-3.5 text-[#00F0FF]" /> Pistas separadas (WAV)</div>
                <div className="grid grid-cols-2 gap-2">
                  {Object.entries(p.result.stems).map(([name, path]) => (
                    <div key={name} className="flex flex-col gap-1" data-testid={`stem-${p.id}-${name}`}>
                      <span className="text-xs text-[#94A3B8] capitalize">{STEM_LABEL[name] || name}</span>
                      <audio src={mediaUrl(path)} controls className="w-full h-8" />
                      <a href={downloadUrl(path, `${p.title}-${name}.wav`)} download={`${p.title}-${name}.wav`}
                        className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#00F0FF] hover:opacity-80">
                        <Download className="w-3 h-3" /> WAV
                      </a>
                    </div>
                  ))}
                </div>
                <div className="mt-3 border-t border-white/8 pt-3" data-testid={`mixer-${p.id}`}>
                  <div className="mono-label mb-2 inline-flex items-center gap-1.5"><SlidersHorizontal className="w-3.5 h-3.5 text-[#00F0FF]" /> Mezclador</div>
                  <div className="flex flex-wrap items-center gap-1.5 mb-3" data-testid={`mix-presets-${p.id}`}>
                    {BUILTIN_PRESETS.map((pr) => (
                      <button key={pr.name} data-testid={`preset-${p.id}-${pr.name.split(" ")[0].toLowerCase()}`} onClick={() => applyPreset(p.id, pr.gains)}
                        className="text-[11px] font-semibold px-2.5 py-1 rounded-full border border-white/12 text-[#94A3B8] hover:border-[#00F0FF] hover:text-[#00F0FF] transition-colors">
                        {pr.name}
                      </button>
                    ))}
                    {presets.map((pr) => (
                      <span key={pr.name} className="inline-flex items-center gap-1 text-[11px] font-semibold pl-2.5 pr-1 py-1 rounded-full border border-[#00F0FF]/30 text-[#00F0FF]">
                        <button data-testid={`custom-preset-${p.id}-${pr.name}`} onClick={() => applyPreset(p.id, pr.gains)}>{pr.name}</button>
                        <button onClick={() => deletePreset(pr.name)} className="hover:text-red-400"><Trash2 className="w-3 h-3" /></button>
                      </span>
                    ))}
                    <button data-testid={`save-preset-${p.id}`} onClick={() => savePreset(p.id)}
                      className="text-[11px] font-semibold px-2.5 py-1 rounded-full border border-dashed border-white/20 text-[#64748B] hover:text-white">
                      + Guardar
                    </button>
                  </div>
                  <div className="space-y-2">
                    {Object.keys(p.result.stems).map((name) => {
                      const g = gainOf(p.id, name);
                      const muted = g === 0;
                      return (
                        <div key={name} className="flex items-center gap-2" data-testid={`mix-row-${p.id}-${name}`}>
                          <button data-testid={`mix-mute-${p.id}-${name}`} onClick={() => setGain(p.id, name, muted ? 100 : 0)}
                            className={`shrink-0 ${muted ? "text-[#FF334B]" : "text-[#94A3B8] hover:text-white"}`}>
                            {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                          </button>
                          <span className="text-xs text-[#94A3B8] w-14 shrink-0">{STEM_LABEL[name] || name}</span>
                          <input data-testid={`mix-gain-${p.id}-${name}`} type="range" min="0" max="150" value={g}
                            onChange={(e) => setGain(p.id, name, Number(e.target.value))}
                            className="flex-1 accent-[#00F0FF]" />
                          <span className="text-[11px] text-[#64748B] w-9 text-right">{g}%</span>
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex items-center gap-2 mt-3">
                    <button data-testid={`mix-export-${p.id}`} onClick={() => runMix(p)} disabled={!!p.busy || busy[p.id] === "mix"}
                      className="inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-full bg-[#00F0FF] text-black hover:opacity-90 transition-colors disabled:opacity-50">
                      {(p.busy === "mix" || busy[p.id] === "mix") ? <Loader2 className="w-4 h-4 spin" /> : <SlidersHorizontal className="w-4 h-4" />} Exportar mezcla (WAV)
                    </button>
                    {p.result?.mix_path && (
                      <a data-testid={`mix-download-${p.id}`} href={downloadUrl(p.result.mix_path, `${p.title}-mezcla.wav`)} download={`${p.title}-mezcla.wav`}
                        className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#00F0FF] hover:opacity-80">
                        <Download className="w-4 h-4" /> Descargar mezcla
                      </a>
                    )}
                  </div>
                  {p.result?.mix_path && <audio src={mediaUrl(p.result.mix_path)} controls className="w-full mt-2 h-9" data-testid={`mix-audio-${p.id}`} />}
                </div>

                <div className="mt-3 border-t border-white/8 pt-3" data-testid={`timeline-${p.id}`}>
                  <div className="mono-label mb-2 inline-flex items-center gap-1.5"><ListOrdered className="w-3.5 h-3.5 text-[#FF9F2E]" /> Timeline multipista (máster)</div>
                  <p className="text-[11px] text-[#64748B] mb-2">Ajusta el desfase de inicio (seg) de cada pista para armar tu máster.</p>
                  <div className="space-y-1.5">
                    {Object.keys(p.result.stems).map((name) => (
                      <div key={name} className="flex items-center gap-2" data-testid={`timeline-row-${p.id}-${name}`}>
                        <span className="text-xs text-[#94A3B8] w-14 shrink-0">{STEM_LABEL[name] || name}</span>
                        <span className="text-[11px] text-[#64748B]">desfase</span>
                        <input data-testid={`timeline-offset-${p.id}-${name}`} type="number" min="0" step="0.5" value={offsetOf(p.id, name)}
                          onChange={(e) => setOffset(p.id, name, Number(e.target.value))}
                          className="w-16 bg-[#0B0C13] border border-white/10 rounded-lg px-2 py-1 text-xs outline-none focus:border-[#FF9F2E]" />
                        <span className="text-[11px] text-[#64748B]">s · vol {gainOf(p.id, name)}%</span>
                      </div>
                    ))}
                    <div className="flex items-center gap-2 pt-1" data-testid={`timeline-extra-${p.id}`}>
                      <span className="text-xs text-[#FF9F2E] w-14 shrink-0">Voz extra</span>
                      {p.result?.timeline_extra ? (
                        <>
                          <span className="text-[11px] text-[#64748B]">desfase</span>
                          <input data-testid={`timeline-offset-${p.id}-extra`} type="number" min="0" step="0.5" value={offsetOf(p.id, "extra")}
                            onChange={(e) => setOffset(p.id, "extra", Number(e.target.value))}
                            className="w-16 bg-[#0B0C13] border border-white/10 rounded-lg px-2 py-1 text-xs outline-none focus:border-[#FF9F2E]" />
                          <input data-testid={`timeline-extra-gain-${p.id}`} type="range" min="0" max="150" value={gainOf(p.id, "extra")}
                            onChange={(e) => setGain(p.id, "extra", Number(e.target.value))} className="flex-1 accent-[#FF9F2E]" />
                          <span className="text-[10px] text-emerald-400">✓ lista</span>
                        </>
                      ) : (
                        <label data-testid={`timeline-upload-${p.id}`} className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full border border-white/12 text-[#94A3B8] hover:border-[#FF9F2E] hover:text-[#FF9F2E] cursor-pointer transition-colors">
                          <Plus className="w-3 h-3" /> Subir voz/narración
                          <input type="file" accept="audio/*" className="hidden" onChange={(e) => uploadExtra(p, e.target.files?.[0])} />
                        </label>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 mt-3" data-testid={`master-loudness-${p.id}`}>
                    <span className="text-[11px] text-[#64748B]">Volumen final:</span>
                    <button data-testid={`loudness-14-${p.id}`} onClick={() => setLoudness((s) => ({ ...s, [p.id]: -14 }))}
                      className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${(loudness[p.id] ?? -14) === -14 ? "border-[#FF9F2E] text-[#FF9F2E] bg-[#FF9F2E]/10" : "border-white/15 text-[#94A3B8] hover:text-white"}`}>
                      Streaming (-14 LUFS)
                    </button>
                    <button data-testid={`loudness-9-${p.id}`} onClick={() => setLoudness((s) => ({ ...s, [p.id]: -9 }))}
                      className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${loudness[p.id] === -9 ? "border-[#FF334B] text-[#FF334B] bg-[#FF334B]/10" : "border-white/15 text-[#94A3B8] hover:text-white"}`}>
                      Club (-9 LUFS)
                    </button>
                  </div>
                  <div className="flex items-center gap-2 mt-3">
                    <button data-testid={`master-export-${p.id}`} onClick={() => runMaster(p)} disabled={!!p.busy || busy[p.id] === "master"}
                      className="inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-full bg-[#FF9F2E] text-black hover:opacity-90 transition-colors disabled:opacity-50">
                      {(p.busy === "master" || busy[p.id] === "master") ? <Loader2 className="w-4 h-4 spin" /> : <ListOrdered className="w-4 h-4" />} Exportar máster (WAV)
                    </button>
                    {p.result?.master_path && (
                      <a data-testid={`master-download-${p.id}`} href={downloadUrl(p.result.master_path, `${p.title}-master.wav`)} download={`${p.title}-master.wav`}
                        className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#FF9F2E] hover:opacity-80">
                        <Download className="w-4 h-4" /> Descargar máster
                      </a>
                    )}
                    {p.result?.master_path && (
                      <button data-testid={`master-promote-${p.id}`} onClick={() => promoteMaster(p)} disabled={p.result?.audio_path === p.result?.master_path}
                        className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#94A3B8] hover:text-white disabled:opacity-40">
                        <ShieldCheck className="w-4 h-4" /> {p.result?.audio_path === p.result?.master_path ? "Es la principal" : "Fijar como principal"}
                      </button>
                    )}
                  </div>
                  {p.result?.master_path && <audio src={mediaUrl(p.result.master_path)} controls className="w-full mt-2 h-9" data-testid={`master-audio-${p.id}`} />}
                </div>

                <div className="mt-3 border-t border-white/8 pt-3" data-testid={`musicvideo-${p.id}`}>
                  <div className="mono-label mb-1 inline-flex items-center gap-1.5"><Clapperboard className="w-3.5 h-3.5 text-[#FF9F2E]" /> Videoclip (Modo Sondo)</div>
                  <p className="text-xs text-[#64748B] mb-2">Genera un videoclip vertical completo con tu personaje, sincronizado a esta canción (calidad Pro 1080p). Un solo clic.</p>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs text-[#94A3B8]">Transición entre escenas:</span>
                    <select data-testid={`mv-transition-${p.id}`} value={mvTransition[p.id] || "dissolve"}
                      onChange={(e) => setMvTransition((m) => ({ ...m, [p.id]: e.target.value }))}
                      className="text-xs bg-white/5 border border-white/12 rounded-lg px-2 py-1.5 text-white focus:border-[#FF9F2E] outline-none">
                      {MV_TRANSITIONS.map((t) => <option key={t.id} value={t.id} className="bg-[#0B0C13]">{t.label}</option>)}
                    </select>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <button data-testid={`musicvideo-btn-${p.id}`} onClick={() => askMusicVideo(p, false)} disabled={busy[p.id] === "musicvideo"}
                      className="inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-full bg-[#FF9F2E] text-black hover:opacity-90 transition-opacity disabled:opacity-50">
                      {busy[p.id] === "musicvideo" ? <Loader2 className="w-4 h-4 spin" /> : <Clapperboard className="w-4 h-4" />}
                      Videoclip rápido
                    </button>
                    <button data-testid={`musicvideo-full-btn-${p.id}`} onClick={() => askMusicVideo(p, true)} disabled={busy[p.id] === "musicvideo"}
                      className="inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-full border border-[#FF9F2E]/50 text-[#FF9F2E] hover:bg-[#FF9F2E]/10 transition-colors disabled:opacity-50">
                      <Clapperboard className="w-4 h-4" /> Videoclip completo (toda la canción)
                    </button>
                  </div>
                </div>

                <div className="mt-3 border-t border-white/8 pt-3" data-testid={`karaoke-${p.id}`}>
                  <div className="mono-label mb-2 inline-flex items-center gap-1.5"><Mic2 className="w-3.5 h-3.5 text-[#FF334B]" /> Karaoke</div>
                  {p.result?.karaoke_path ? (
                    <>
                      <video src={mediaUrl(p.result.karaoke_path)} controls className="w-full max-w-[240px] rounded-lg bg-black" data-testid={`karaoke-video-${p.id}`} />
                      <div className="flex items-center gap-3 mt-2">
                        <a data-testid={`karaoke-download-${p.id}`} href={downloadUrl(p.result.karaoke_path, `${p.title}-karaoke.mp4`)} download={`${p.title}-karaoke.mp4`}
                          className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#FF334B] hover:opacity-80">
                          <Download className="w-4 h-4" /> Descargar karaoke
                        </a>
                        <button data-testid={`karaoke-regen-${p.id}`} onClick={() => runKaraoke(p)} disabled={!!p.busy}
                          className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#94A3B8] hover:text-white disabled:opacity-40">
                          <RefreshCw className="w-4 h-4" /> Regenerar
                        </button>
                      </div>
                      <div className="flex flex-wrap items-center gap-1.5 mt-2">
                        <span className="text-[11px] text-[#64748B] mr-1">Compartir:</span>
                        <button data-testid={`karaoke-share-tiktok-${p.id}`} onClick={() => shareTo(p, p.result.karaoke_path, "tiktok")} title="TikTok"
                          className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-full border border-white/20 text-white hover:bg-white/10 transition-colors">
                          <Music2 className="w-3.5 h-3.5" /> TikTok
                        </button>
                        <button data-testid={`karaoke-share-reels-${p.id}`} onClick={() => shareTo(p, p.result.karaoke_path, "instagram")} title="Reels"
                          className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-full border border-[#E1306C]/40 text-[#E1306C] hover:bg-[#E1306C]/10 transition-colors">
                          <Instagram className="w-3.5 h-3.5" /> Reels
                        </button>
                        <button data-testid={`karaoke-share-whatsapp-${p.id}`} onClick={() => shareTo(p, p.result.karaoke_path, "whatsapp")} title="WhatsApp"
                          className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-full border border-[#25D366]/40 text-[#25D366] hover:bg-[#25D366]/10 transition-colors">
                          <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
                        </button>
                      </div>
                    </>
                  ) : (
                    <button data-testid={`karaoke-generate-${p.id}`} onClick={() => runKaraoke(p)} disabled={!!p.busy || busy[p.id] === "karaoke"}
                      className="inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-full bg-[#FF334B] text-white hover:bg-[#E0223A] transition-colors disabled:opacity-50">
                      {(p.busy === "karaoke" || busy[p.id] === "karaoke") ? <Loader2 className="w-4 h-4 spin" /> : <Mic2 className="w-4 h-4" />} Generar karaoke (video vertical)
                    </button>
                  )}
                  <p className="text-[11px] text-[#64748B] mt-1.5">Quita la voz y añade la letra como subtítulos sobre un video 9:16 listo para compartir.</p>
                </div>
              </div>
            ) : (
              <button data-testid={`stems-project-${p.id}`} onClick={() => separateStems(p)} disabled={!!p.busy || busy[p.id] === "stems"}
                className="inline-flex items-center gap-2 text-sm font-semibold px-4 py-2 rounded-full border border-[#00F0FF]/40 text-[#00F0FF] hover:bg-[#00F0FF]/10 transition-colors disabled:opacity-50">
                {(p.busy === "stems" || busy[p.id] === "stems") ? <Loader2 className="w-4 h-4 spin" /> : <Music2 className="w-4 h-4" />} Separar pistas (stems)
              </button>
            )}
          </div>
          {covers.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-4" data-testid={`animated-cover-${p.id}`}>
              {covers.map((cv) => (
                <div key={cv.label} className="flex flex-col gap-2">
                  <div className="mono-label inline-flex items-center gap-1.5"><Waves className="w-3.5 h-3.5 text-[#00F0FF]" /> {cv.label}</div>
                  <video src={mediaUrl(cv.path)} controls playsInline
                    className="w-40 rounded-xl border border-white/10 bg-black" style={{ aspectRatio: cv.ar }} />
                  <div className="flex flex-wrap items-center gap-1.5 max-w-40">
                    <a href={downloadUrl(cv.path, `${p.title}-${cv.ar.replace("/", "x")}.mp4`)} download={`${p.title}-${cv.ar.replace("/", "x")}.mp4`}
                      data-testid={`download-animated-${p.id}`} title="Descargar"
                      className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-full border border-white/12 hover:border-[#00F0FF] hover:text-[#00F0FF] transition-colors">
                      <Download className="w-3.5 h-3.5" />
                    </a>
                    <button data-testid={`share-whatsapp-${p.id}`} onClick={() => shareTo(p, cv.path, "whatsapp")} title="WhatsApp"
                      className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-full border border-[#25D366]/40 text-[#25D366] hover:bg-[#25D366]/10 transition-colors">
                      <MessageCircle className="w-3.5 h-3.5" />
                    </button>
                    <button data-testid={`share-instagram-${p.id}`} onClick={() => shareTo(p, cv.path, "instagram")} title="Instagram"
                      className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-full border border-[#E1306C]/40 text-[#E1306C] hover:bg-[#E1306C]/10 transition-colors">
                      <Instagram className="w-3.5 h-3.5" />
                    </button>
                    <button data-testid={`share-tiktok-${p.id}`} onClick={() => shareTo(p, cv.path, "tiktok")} title="TikTok"
                      className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-full border border-white/20 text-white hover:bg-white/10 transition-colors">
                      <Music2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      );
    }
    if (p.type === "video" && p.result?.video_path)
      return <video src={mediaUrl(p.result.video_path)} poster={mediaUrl(p.result?.poster_path)} controls className="w-full rounded-lg mt-3 bg-black" />;
    if (p.type === "shortfilm") {
      const sc = p.scenes || [];
      if (p.result?.export_path)
        return <video src={mediaUrl(p.result.export_path)} poster={mediaUrl(p.result?.poster_path)} controls className="w-full rounded-lg mt-3 bg-black" data-testid={`film-video-${p.id}`} />;
      if (sc.length <= 1) {
        const clip = sc.find((s) => s.video_path)?.video_path;
        return clip ? <video src={mediaUrl(clip)} poster={mediaUrl(p.result?.poster_path)} controls className="w-full rounded-lg mt-3 bg-black" /> : null;
      }
      // Nunca mostrar la escena 1 (5s) como si fuera la película completa.
      return (
        <div className="mt-3 rounded-lg border border-dashed border-[#FF9F2E]/40 bg-[#FF9F2E]/5 p-4 text-sm text-[#FFB869]" data-testid={`film-not-assembled-${p.id}`}>
          El video final todavía no está unido ({sc.filter((s) => s.video_path).length} de {sc.length} escenas listas). Usa el botón «{sc.some((s) => !s.video_path) ? "Completar escenas" : "Ensamblar video (gratis)"}» de abajo.
        </div>
      );
    }
    return null;
  };

  // Proyectos con el MISMO título (ej. "Mi cortometraje" ×2) se numeran #1, #2… por fecha para distinguirlos.
  const titleKey = (p) => (p.title || "").trim().toLowerCase();
  const dupInfo = (() => {
    const cnt = {}, seq = {};
    [...projects].sort((a, b) => (a.created_at || "").localeCompare(b.created_at || "")).forEach((p) => {
      const k = titleKey(p);
      cnt[k] = (cnt[k] || 0) + 1;
      seq[p.id] = cnt[k];
    });
    return { cnt, seq };
  })();

  return (
    <div className="max-w-5xl mx-auto space-y-6 fade-up">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-head text-2xl sm:text-3xl font-bold tracking-tight">Mis proyectos</h2>
        <div className="flex flex-wrap items-center gap-2">
          {projects.some((p) => p.status === "failed") && (
            <button data-testid="retry-all-button" onClick={retryAll}
              className="inline-flex items-center gap-2 font-semibold px-4 py-2.5 rounded-full border border-[#FF9F2E]/50 text-[#FF9F2E] hover:bg-[#FF9F2E]/10 transition-colors">
              <RefreshCw className="w-4 h-4" /> Reintentar todo
            </button>
          )}
          <button data-testid="new-project-button" onClick={() => goTo("cancion")}
            className="inline-flex items-center gap-2 bg-[#FF334B] hover:bg-[#E0223A] text-white font-semibold px-4 py-2.5 rounded-full transition-colors">
            <Plus className="w-4 h-4" /> Nuevo proyecto
          </button>
        </div>
      </div>

      {queue.length > 0 && (
        <div className="mfp-card p-5" data-testid="queue-panel">
          <div className="flex items-center gap-2 mb-3">
            <ListOrdered className="w-4 h-4 text-[#FF9F2E]" />
            <h3 className="font-head font-semibold">Cola de espera</h3>
            <span className="mono-label ml-1">{queue.length} en espera · arrastra para reordenar</span>
          </div>
          <Reorder.Group axis="y" values={queue} onReorder={reorder} className="space-y-2">
            {queue.map((item) => <QueueRow key={item.pid} item={item} />)}
          </Reorder.Group>
        </div>
      )}

      {loadError ? (
        <div className="mfp-card border-dashed p-14 text-center" data-testid="projects-error">
          <AlertTriangle className="w-12 h-12 mx-auto text-[#FF334B] mb-3" />
          <p className="text-lg font-head">No pudimos cargar tus proyectos.</p>
          <p className="text-sm text-[#64748B] mb-4">Revisa tu conexión e inténtalo de nuevo.</p>
          <button data-testid="projects-retry-button" onClick={reload}
            className="inline-flex items-center gap-2 bg-[#FF334B] hover:bg-[#E0223A] text-white font-semibold px-4 py-2.5 rounded-full transition-colors">
            <RefreshCw className="w-4 h-4" /> Reintentar
          </button>
        </div>
      ) : loading ? (
        <div className="grid md:grid-cols-2 gap-4" data-testid="projects-skeleton">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="mfp-card p-5 animate-pulse">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-white/8" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 w-2/3 rounded bg-white/8" />
                  <div className="h-3 w-1/3 rounded bg-white/6" />
                </div>
              </div>
              <div className="h-9 w-full rounded-lg bg-white/6 mt-4" />
            </div>
          ))}
        </div>
      ) : projects.length === 0 ? (
        <div className="mfp-card border-dashed p-14 text-center">
          <FolderOpen className="w-12 h-12 mx-auto text-[#334155] mb-3" />
          <p className="text-lg font-head">Aún no tienes proyectos.</p>
          <p className="text-sm text-[#64748B]">Crea tu primera canción, video o cortometraje.</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {projects.map((p) => {
            const T = TYPE[p.type] || TYPE.video;
            const Icon = T.icon;
            const poster = p.result?.poster_path;
            const b = busy[p.id];
            const readyScenes = (p.scenes || []).filter((s) => s.video_path);
            const isFilm = p.type === "shortfilm" && p.status === "ready";
            const missingScenes = isFilm ? (p.scenes || []).length - readyScenes.length : 0;
            const needsAssembly = isFilm && (p.scenes || []).length > 1 && (!p.result?.export_path || p.result?.export_stale);
            return (
              <div key={p.id} className="mfp-card p-5 min-w-0" data-testid={`project-card-${p.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    {poster ? (
                      <img src={mediaUrl(poster)} alt={p.title} className="w-12 h-12 rounded-xl object-cover shrink-0" data-testid={`project-cover-${p.id}`} />
                    ) : (
                      <div className="w-12 h-12 rounded-xl bg-[#FF334B]/12 flex items-center justify-center shrink-0"><Icon className="w-5 h-5 text-[#FF334B]" /></div>
                    )}
                    <div className="min-w-0">
                      <div className="font-head font-semibold truncate">
                        {p.title}
                        {dupInfo.cnt[titleKey(p)] > 1 && <span className="ml-1.5 text-xs font-semibold text-[#FF9F2E]" data-testid={`project-dup-${p.id}`}>#{dupInfo.seq[p.id]}</span>}
                      </div>
                      <div className="mono-label">{T.label}</div>
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-1 text-[11px] text-[#64748B]" data-testid={`project-meta-${p.id}`}>
                        {p.created_at && <span>{new Date(p.created_at).toLocaleString("es", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</span>}
                        {p.meta?.quality === "pro" && <span className="px-1.5 py-0.5 rounded-full bg-[#FF9F2E]/15 text-[#FF9F2E] font-semibold">Pro 1080p</span>}
                        {p.type === "shortfilm" && p.meta?.aspect && <span className="px-1.5 py-0.5 rounded-full bg-white/8 text-[#94A3B8] font-semibold">{p.meta.aspect}</span>}
                        {p.meta?.character_name && <span className="px-1.5 py-0.5 rounded-full bg-[#00F0FF]/12 text-[#00F0FF] font-semibold" data-testid={`project-character-${p.id}`}>{p.meta.character_name}</span>}
                        {(p.meta?.prompt || p.meta?.genre) && <span className="truncate max-w-[220px]">· {p.meta.prompt || p.meta.genre}</span>}
                        {p.status === "ready" && p.gen_seconds != null && (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-500/12 text-emerald-400 font-semibold" data-testid={`gen-time-${p.id}`}>
                            <Timer className="w-3 h-3" /> Generado en {p.gen_seconds >= 60 ? `${Math.floor(p.gen_seconds / 60)}m ${p.gen_seconds % 60}s` : `${p.gen_seconds}s`}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <StatusBadge status={p.status} retry={p.retry} />
                </div>

                {p.status === "processing" && p.retry && (
                  <p className="text-xs text-[#FF9F2E] mt-3">Servicio saturado — reintentando automáticamente ({p.retry.attempt}/{p.retry.max}). Te avisaremos al terminar.</p>
                )}
                {p.status === "processing" && !p.retry && p.progress?.label && (
                  <div className="mt-3" data-testid={`progress-${p.id}`}>
                    <p className="text-xs text-[#FF9F2E] mb-1">{p.progress.label}</p>
                    {p.progress.total > 0 && (
                      <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
                        <div className="h-full bg-[#FF9F2E] transition-all" style={{ width: `${Math.round((p.progress.current / p.progress.total) * 100)}%` }} />
                      </div>
                    )}
                  </div>
                )}
                {(p.status === "processing" || p.status === "queued") && (
                  <button data-testid={`cancel-project-${p.id}`} onClick={() => cancelJob(p.id)}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 mt-3 rounded-full border border-[#FF334B]/50 text-[#FF334B] hover:bg-[#FF334B]/10 transition-colors">
                    <AlertTriangle className="w-3.5 h-3.5" /> Cancelar y reembolsar
                  </button>
                )}
                {p.status === "failed" && <p className="text-red-400 text-sm mt-3">{p.error}</p>}
                {p.status === "ready" && p.error && <p className="text-[#FF9F2E] text-xs mt-3" data-testid={`project-note-${p.id}`}>{p.error}</p>}
                {(p.billing?.refunded || 0) > 0 && (
                  <p data-testid={`refunded-badge-${p.id}`} className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-400">
                    <RotateCcw className="w-3.5 h-3.5" /> Reembolsado {Math.round(Number(p.billing.refunded))} créditos
                  </p>
                )}
                {preview(p)}
                {p.type === "shortfilm" && (p.busy === "trailer" || p.result?.trailer_path || p.busy_error) && (
                  <div className="mt-3"><TrailerResult project={p} filename={p.title} /></div>
                )}

                {p.status === "ready" && p.result?.variants?.length > 1 && (
                  <div className="flex flex-wrap items-center gap-2 mt-3" data-testid={`variants-${p.id}`}>
                    <span className="mono-label">{p.type === "video" ? "Toma:" : "Versión:"}</span>
                    {p.result.variants.map((v, i) => {
                      const active = p.type === "video" ? p.result.video_path === v.video_path : p.result.audio_path === v.audio_path;
                      return (
                        <button key={i} data-testid={`variant-${p.id}-${i}`} onClick={() => selectVariant(p, i)}
                          className={`text-xs font-semibold px-3 py-1.5 rounded-full transition-colors ${active ? "bg-[#FF334B] text-white" : "border border-white/12 hover:border-[#FF334B] hover:text-[#FF334B]"}`}>
                          {v.label || `V${i + 1}`}
                        </button>
                      );
                    })}
                  </div>
                )}
                {p.busy && (
                  <p className="text-xs text-[#FF9F2E] mt-3 inline-flex items-center gap-2" data-testid={`busy-${p.id}`}>
                    <Loader2 className="w-3.5 h-3.5 spin" />
                    {p.busy === "remix" ? "Remezclando una nueva versión…" : p.busy === "extend" ? "Extendiendo tu canción…" : p.busy === "stems" ? "Separando pistas…" : p.busy === "mix" ? "Exportando tu mezcla…" : p.busy === "master" ? "Generando tu máster…" : p.busy === "karaoke" ? "Creando tu karaoke…" : "Creando tu portada animada…"}
                  </p>
                )}
                {p.busy_error && <p className="text-red-400 text-xs mt-2" data-testid={`busy-error-${p.id}`}>{p.busy_error}</p>}
                {extendOpen === p.id && (
                  <div className="mt-3 flex items-center gap-2" data-testid={`extend-picker-${p.id}`}>
                    <span className="mono-label">Duración:</span>
                    {EXTEND_OPTS.map((o) => (
                      <button key={o.s} data-testid={`extend-opt-${p.id}-${o.s}`} onClick={() => extend(p, o.s)}
                        className="text-xs font-semibold px-3 py-1.5 rounded-full border border-[#FF9F2E]/50 text-[#FF9F2E] hover:bg-[#FF9F2E]/10 transition-colors">
                        {o.label}
                      </button>
                    ))}
                  </div>
                )}
                {animOpen === p.id && (
                  <div className="mt-3 flex items-center gap-2" data-testid={`anim-picker-${p.id}`}>
                    <span className="mono-label">Formato:</span>
                    <button data-testid={`anim-opt-${p.id}-vertical`} onClick={() => animateCover(p, "vertical")}
                      className="text-xs font-semibold px-3 py-1.5 rounded-full border border-[#00F0FF]/50 text-[#00F0FF] hover:bg-[#00F0FF]/10 transition-colors">
                      9:16 Stories
                    </button>
                    <button data-testid={`anim-opt-${p.id}-square`} onClick={() => animateCover(p, "square")}
                      className="text-xs font-semibold px-3 py-1.5 rounded-full border border-[#00F0FF]/50 text-[#00F0FF] hover:bg-[#00F0FF]/10 transition-colors">
                      1:1 Feed
                    </button>
                  </div>
                )}
                {chooser === p.id && readyScenes.length > 0 && (
                  <div className="mt-3 flex gap-2 overflow-x-auto pb-1" data-testid={`scene-chooser-${p.id}`}>
                    {(p.scenes || []).map((s, i) => s.video_path && (
                      <button key={i} data-testid={`choose-scene-${p.id}-${i}`} onClick={() => pickScene(p, i)}
                        className="shrink-0 w-28 rounded-lg overflow-hidden border-2 border-white/10 hover:border-[#FF334B] transition-colors">
                        <video src={mediaUrl(s.video_path)} muted className="w-full aspect-video object-cover bg-black" />
                        <div className="text-[0.6rem] py-1 bg-[#0B0C13]">Escena {i + 1}</div>
                      </button>
                    ))}
                  </div>
                )}

                {coverOpts?.id === p.id && (
                  <div className="mt-3" data-testid={`cover-gallery-${p.id}`}>
                    <div className="mono-label mb-2">Elige tu portada</div>
                    <div className="flex gap-2 overflow-x-auto pb-1">
                      {coverOpts.options.map((opt, i) => (
                        <button key={i} data-testid={`cover-option-${p.id}-${i}`} onClick={() => selectCover(p, opt)}
                          className={`shrink-0 rounded-lg overflow-hidden border-2 transition-colors ${p.result?.poster_path === opt ? "border-[#FF334B]" : "border-white/10 hover:border-[#FF334B]/60"}`}>
                          <img src={mediaUrl(opt)} alt={`Portada ${i + 1}`} className="w-24 h-24 object-cover" />
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex flex-wrap items-center gap-2 mt-4">
                  {p.status === "ready" && (
                    <button data-testid={`download-project-${p.id}`} onClick={() => doDownload(p, "native", "")} disabled={b === "dl"}
                      className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-full bg-white text-black hover:bg-white/90 transition-colors disabled:opacity-60">
                      {b === "dl" ? <Loader2 className="w-3.5 h-3.5 spin" /> : <Download className="w-3.5 h-3.5" />} Descargar
                    </button>
                  )}
                  {p.status === "ready" && p.type !== "song" && (
                    <button data-testid={`download916-project-${p.id}`} onClick={() => doDownload(p, "9:16", "-9x16")} disabled={b === "dl916"}
                      className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-full border border-white/12 hover:border-[#FF334B] hover:text-[#FF334B] transition-colors disabled:opacity-60">
                      {b === "dl916" ? <Loader2 className="w-3.5 h-3.5 spin" /> : <Smartphone className="w-3.5 h-3.5" />} 9:16 Reels
                    </button>
                  )}
                  {p.status === "ready" && p.type !== "song" && (
                    <button data-testid={`edit-project-${p.id}`} onClick={() => setEditor(p)}
                      className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-full border border-[#FF334B]/40 text-[#FF334B] hover:bg-[#FF334B]/10 transition-colors">
                      <Scissors className="w-3.5 h-3.5" /> Editar
                    </button>
                  )}
                  {p.status === "ready" && p.type === "shortfilm" && (p.scenes || []).length > 0 && (
                    <button data-testid={`scenes-toggle-${p.id}`} onClick={() => setScenesOpen(scenesOpen === p.id ? null : p.id)}
                      className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-full border border-white/12 hover:border-[#FF9F2E] hover:text-[#FF9F2E] transition-colors">
                      <Clapperboard className="w-3.5 h-3.5" /> Escenas
                    </button>
                  )}
                  {p.status === "ready" && (
                    <button data-testid={`share-project-${p.id}`} onClick={() => openShare(p)}
                      className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-full border border-white/12 hover:border-[#FF334B] hover:text-[#FF334B] transition-colors">
                      <Share2 className="w-3.5 h-3.5" /> Compartir
                    </button>
                  )}
                  {p.status === "ready" && (
                    p.published ? (
                      <div className="inline-flex items-center gap-1.5">
                        <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-400"><Globe className="w-3.5 h-3.5" /> En Comunidad</span>
                        <button data-testid={`unpublish-project-${p.id}`} onClick={() => unpublishProject(p)} disabled={b === "publish"}
                          className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-full border border-white/12 hover:border-[#FF334B] hover:text-[#FF334B] transition-colors disabled:opacity-60">
                          {b === "publish" ? <Loader2 className="w-3.5 h-3.5 spin" /> : null} Quitar
                        </button>
                      </div>
                    ) : (
                      <div className="inline-flex items-center gap-1.5">
                        <select data-testid={`publish-category-${p.id}`} value={pubCat[p.id] || (p.type === "song" ? "cancion" : "video")}
                          onChange={(e) => setPubCat((m) => ({ ...m, [p.id]: e.target.value }))}
                          className="text-xs bg-white/5 border border-white/12 rounded-full px-2.5 py-2 text-white focus:border-[#FF334B] outline-none">
                          <option value="video" className="bg-[#0B0C13]">Video</option>
                          <option value="cancion" className="bg-[#0B0C13]">Canción</option>
                          <option value="corto" className="bg-[#0B0C13]">Cortometraje</option>
                          <option value="anuncio" className="bg-[#0B0C13]">Anuncio</option>
                        </select>
                        <button data-testid={`publish-project-${p.id}`} onClick={() => publishProject(p)} disabled={b === "publish"}
                          className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-full bg-[#FF334B] text-white hover:bg-[#E0223A] transition-colors disabled:opacity-60">
                          {b === "publish" ? <Loader2 className="w-3.5 h-3.5 spin" /> : <Globe className="w-3.5 h-3.5" />} Publicar
                        </button>
                      </div>
                    )
                  )}
                  {p.status === "ready" && p.type === "song" && (
                    <button data-testid={`aicover-project-${p.id}`} onClick={() => aiCover(p)} disabled={b === "cover"}
                      className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-full border border-[#FF9F2E]/50 text-[#FF9F2E] hover:bg-[#FF9F2E]/10 transition-colors disabled:opacity-60">
                      {b === "cover" ? <Loader2 className="w-3.5 h-3.5 spin" /> : <Sparkles className="w-3.5 h-3.5" />} Portada IA
                    </button>
                  )}
                  {p.status === "ready" && p.type === "song" && p.result?.lyrics && (
                    <button data-testid={`lyrics-toggle-${p.id}`} onClick={() => setLyricsOpen(lyricsOpen === p.id ? null : p.id)}
                      className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-full border border-white/12 hover:border-[#00F0FF] hover:text-[#00F0FF] transition-colors">
                      <FileText className="w-3.5 h-3.5" /> Letra
                    </button>
                  )}
                  {p.status === "ready" && p.type === "song" && (
                    <button data-testid={`reedit-song-${p.id}`} onClick={() => reeditSong(p)}
                      className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-full border border-[#FF334B]/40 text-[#FF334B] hover:bg-[#FF334B]/10 transition-colors">
                      <Pencil className="w-3.5 h-3.5" /> Rehacer
                    </button>
                  )}
                  {p.status === "ready" && p.type === "song" && p.meta?.voice !== "mine" && (
                    <>
                      <button data-testid={`remix-project-${p.id}`} onClick={() => remix(p)} disabled={!!p.busy || b === "remix"}
                        className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-full border border-white/12 hover:border-[#FF334B] hover:text-[#FF334B] transition-colors disabled:opacity-60">
                        {b === "remix" ? <Loader2 className="w-3.5 h-3.5 spin" /> : <Shuffle className="w-3.5 h-3.5" />} Remezclar
                      </button>
                      <button data-testid={`extend-project-${p.id}`} onClick={() => setExtendOpen(extendOpen === p.id ? null : p.id)} disabled={!!p.busy}
                        className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-full border border-white/12 hover:border-[#FF334B] hover:text-[#FF334B] transition-colors disabled:opacity-60">
                        <Timer className="w-3.5 h-3.5" /> Extender
                      </button>
                      <button data-testid={`animate-cover-${p.id}`} onClick={() => setAnimOpen(animOpen === p.id ? null : p.id)} disabled={!!p.busy || b === "animcover"}
                        className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-full border border-[#00F0FF]/40 text-[#00F0FF] hover:bg-[#00F0FF]/10 transition-colors disabled:opacity-60">
                        {b === "animcover" ? <Loader2 className="w-3.5 h-3.5 spin" /> : <Waves className="w-3.5 h-3.5" />} Portada animada
                      </button>
                    </>
                  )}
                  {p.status === "ready" && p.type === "shortfilm" && readyScenes.length > 0 && (
                    <button data-testid={`choose-poster-${p.id}`} onClick={() => setChooser(chooser === p.id ? null : p.id)}
                      className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-full border border-white/12 hover:border-[#FF334B] hover:text-[#FF334B] transition-colors">
                      <ImageIcon className="w-3.5 h-3.5" /> Portada
                    </button>
                  )}
                  {p.status === "ready" && (
                    <button data-testid={`receipt-project-${p.id}`} onClick={() => toggleReceipt(p)}
                      className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-full border border-white/12 hover:border-[#FF9F2E] hover:text-[#FF9F2E] transition-colors">
                      <Receipt className="w-3.5 h-3.5" /> Recibo
                    </button>
                  )}
                  {p.status === "ready" && (
                    <button data-testid={`certificate-project-${p.id}`} onClick={() => openCertificate(p)}
                      className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-full border border-white/12 hover:border-emerald-400 hover:text-emerald-400 transition-colors">
                      <ShieldCheck className="w-3.5 h-3.5" /> Certificado
                    </button>
                  )}
                  {isFilm && readyScenes.length >= 2 && !p.busy && (
                    <button data-testid={`trailer-project-${p.id}`} onClick={() => setTrailerFor(p)}
                      className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-full border border-[#FF334B]/50 text-[#FF7A8A] hover:bg-[#FF334B]/10 transition-colors">
                      <Clapperboard className="w-3.5 h-3.5" /> {p.result?.trailer_path ? "Nuevo tráiler" : "Tráiler 30 s"}
                    </button>
                  )}
                  {(missingScenes > 0 || needsAssembly) && (
                    <button data-testid={`complete-project-${p.id}`} onClick={() => retry(p)}
                      className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-full bg-[#FF9F2E] text-black hover:opacity-90 transition-opacity">
                      <Wrench className="w-3.5 h-3.5" /> {missingScenes > 0 ? `Completar escenas (${missingScenes})` : "Ensamblar video (gratis)"}
                    </button>
                  )}
                  {p.status === "failed" && (
                    <button data-testid={`retry-project-${p.id}`} onClick={() => retry(p)}
                      className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-full border border-[#FF9F2E]/50 text-[#FF9F2E] hover:bg-[#FF9F2E]/10 transition-colors">
                      <RefreshCw className="w-3.5 h-3.5" /> Reintentar
                    </button>
                  )}
                  <button data-testid={`delete-project-${p.id}`} onClick={() => del(p)}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-full border border-white/12 hover:border-red-400 hover:text-red-400 transition-colors ml-auto">
                    <Trash2 className="w-3.5 h-3.5" /> Borrar
                  </button>
                </div>

                {scenesOpen === p.id && (p.scenes || []).length > 0 && (
                  <div className="mt-3 mfp-elev rounded-xl p-4" data-testid={`scenes-panel-${p.id}`}>
                    <div className="mono-label mb-2 inline-flex items-center gap-1.5"><Clapperboard className="w-3.5 h-3.5 text-[#FF9F2E]" /> Escenas · regenera solo la que quieras</div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {(p.scenes || []).map((s, i) => (
                        <div key={i} className="rounded-lg border border-white/10 overflow-hidden bg-[#0B0C13]" data-testid={`scene-item-${p.id}-${i}`}>
                          {s.video_path ? (
                            <video src={mediaUrl(s.video_path)} controls playsInline className="w-full aspect-video object-cover bg-black" data-testid={`scene-video-${p.id}-${i}`} />
                          ) : (
                            <div className={`w-full aspect-video flex flex-col items-center justify-center gap-1 px-2 text-center text-[10px] ${s.status === "failed" ? "text-red-300 bg-red-500/5" : "text-[#64748B]"}`}
                              data-testid={`scene-state-${p.id}-${i}`} title={s.error || ""}>
                              {s.status === "processing" ? "Generando…" : s.status === "failed" ? (<><AlertTriangle className="w-4 h-4" /> Falló — no generada{s.error ? <span className="line-clamp-2 opacity-80">{s.error}</span> : null}</>) : "Sin generar"}
                            </div>
                          )}
                          <div className="p-2 space-y-1.5" data-testid={`scene-trace-${p.id}-${i}`}>
                            <div className="flex items-center justify-between gap-2">
                              <span className="text-[11px] text-[#94A3B8]" data-testid={`scene-duration-${p.id}-${i}`}>
                                Escena {i + 1} · {s.real_duration ? `${Math.round(s.real_duration)}s reales` : s.type === "dialogue" ? "diálogo" : `${s.duration || 6}s plan`}
                              </span>
                              <button data-testid={`regen-scene-${p.id}-${i}`} onClick={() => regenScene(p, i)} disabled={b === `regen-${i}`}
                                className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-full border border-[#FF9F2E]/50 text-[#FF9F2E] hover:bg-[#FF9F2E]/10 transition-colors disabled:opacity-50">
                                {b === `regen-${i}` ? <Loader2 className="w-3 h-3 spin" /> : <RefreshCw className="w-3 h-3" />} Regenerar
                              </button>
                            </div>
                            {(s.character_ids || []).length > 0 ? (
                              <div className="flex flex-wrap items-center gap-1">
                                {(s.character_ids || []).map((cid) => (
                                  <span key={cid} className="inline-flex items-center gap-1 pl-0.5 pr-1.5 py-0.5 rounded-full bg-white/5 text-[10px] text-[#CBD5E1]">
                                    {charMap[cid]?.photo_path ? <img src={mediaUrl(charMap[cid].photo_path)} alt="" className="w-4 h-4 rounded-full object-cover" /> : <UserCircle2 className="w-3.5 h-3.5" />}
                                    {charMap[cid]?.name || "Personaje"}
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <span className="text-[10px] text-[#64748B]">Plano de ambiente (sin personaje)</span>
                            )}
                            {(s.description || s.raw) && (
                              <p className="text-[10px] text-[#64748B] line-clamp-2" title={s.raw || s.description}>{s.raw || s.description}</p>
                            )}
                            {s.model && <div className="text-[10px] text-[#00F0FF]/80 truncate" data-testid={`scene-model-${p.id}-${i}`}>{s.model}</div>}
                            <div className="flex items-center justify-between text-[10px] text-[#64748B]">
                              <span>{(p.updated_at || p.created_at || "").slice(0, 10)}</span>
                              {s.video_path
                                ? <span className="inline-flex items-center gap-1 text-[#FF9F2E]" title="Créditos aprox. de esta escena"><Coins className="w-3 h-3" />{fmtCredits(filmSceneCredits(s))}</span>
                                : <span className="text-[#64748B]" data-testid={`scene-nocharge-${p.id}-${i}`}>sin cobro</span>}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    <p className="text-[11px] text-[#64748B] mt-2">Regenerar una escena cuesta solo esa escena; las demás se conservan. Si falla, se reembolsa.</p>
                  </div>
                )}

                {lyricsOpen === p.id && p.result?.lyrics && (
                  <div className="mt-3 mfp-elev rounded-xl p-4" data-testid={`lyrics-panel-${p.id}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="mono-label inline-flex items-center gap-1.5"><FileText className="w-3.5 h-3.5 text-[#00F0FF]" /> Letra</div>
                      <button data-testid={`lyrics-copy-${p.id}`} onClick={() => copyLyrics(p)}
                        className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border border-white/12 hover:border-[#00F0FF] hover:text-[#00F0FF] transition-colors">
                        <Copy className="w-3.5 h-3.5" /> Copiar
                      </button>
                    </div>
                    <textarea data-testid={`lyrics-text-${p.id}`} readOnly value={p.result.lyrics} rows={8}
                      className="w-full bg-[#0B0C13] border border-white/10 rounded-lg px-3 py-2 text-sm text-[#CBD5E1] whitespace-pre-wrap resize-y outline-none focus:border-[#00F0FF]" />
                  </div>
                )}

                {receipt[p.id] && (
                  <div className="mt-3 mfp-elev rounded-xl p-4" data-testid={`receipt-${p.id}`}>
                    <div className="mono-label mb-2 inline-flex items-center gap-1.5"><Receipt className="w-3.5 h-3.5 text-[#FF9F2E]" /> Recibo del trabajo</div>
                    {receipt[p.id] === "loading" ? (
                      <div className="text-[#64748B] text-sm inline-flex items-center gap-2"><Loader2 className="w-4 h-4 spin" /> Cargando…</div>
                    ) : (
                      <div className="space-y-3 text-sm">
                        <div>
                          <div className="text-[11px] text-[#64748B] mb-1">Créditos de la app</div>
                          {(receipt[p.id].credits || []).length === 0 ? (
                            <div className="text-[#94A3B8]" data-testid={`receipt-no-credits-${p.id}`}>
                              {receipt[p.id].admin ? "Sin cargos de créditos (cuenta de administrador)." : "No se descontaron créditos por este trabajo."}
                            </div>
                          ) : (
                            <div className="space-y-1.5">
                              {receipt[p.id].credits.map((e) => (
                                <div key={e.id} className="flex items-center justify-between gap-3">
                                  <div className="text-[#94A3B8]">
                                    {e.type === "refund" ? "Reembolso" : e.type === "grant" ? "Recarga" : "Cargo"}
                                    {e.note ? <span className="text-[#64748B]"> · {e.note}</span> : null}
                                  </div>
                                  <div className={(e.type === "refund" || e.type === "grant") ? "text-emerald-400 font-semibold whitespace-nowrap" : "font-semibold whitespace-nowrap"}>
                                    {(e.type === "refund" || e.type === "grant") ? "+" : "−"}{e.credits} cr
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        {receipt[p.id].fal && (
                          <div className="border-t border-white/8 pt-2" data-testid={`receipt-fal-${p.id}`}>
                            <div className="flex items-center justify-between">
                              <span className="text-[11px] text-[#64748B]">Costo real facturado por Fal{receipt[p.id].byok ? " (tu cuenta)" : ""}</span>
                              <span className="font-bold text-[#FF334B]" data-testid={`receipt-fal-total-${p.id}`}>${Number(receipt[p.id].fal.total_usd).toFixed(2)} USD</span>
                            </div>
                            <div className="text-[11px] text-[#64748B]">{receipt[p.id].fal.calls} llamada(s) registradas</div>
                            {(receipt[p.id].fal.by_model || []).map((m) => (
                              <div key={m.model} className="flex justify-between text-xs mt-1">
                                <span className="text-[#94A3B8]">{m.model} · {m.calls}× · {m.seconds}s</span>
                                <span className="text-[#CBD5E1]">${Number(m.usd).toFixed(2)}</span>
                              </div>
                            ))}
                          </div>
                        )}
                        {receipt[p.id].error && <div className="text-red-400 text-xs">No se pudo cargar el recibo.</div>}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <ShareDialog open={share.open} onOpenChange={(v) => setShare((s) => ({ ...s, open: v }))} project={share.project} />
      {editor && <VideoEditor project={editor} songs={readySongs} onClose={() => setEditor(null)} onSaved={reload} />}

      <AlertDialog open={!!mvConfirm} onOpenChange={(v) => !v && setMvConfirm(null)}>
        <AlertDialogContent data-testid="mv-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar videoclip</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p>Se generará un videoclip vertical con tu personaje sincronizado a la canción.</p>
                <div className="rounded-xl border border-white/10 bg-white/5 p-3 space-y-1.5 text-sm">
                  <div className="flex justify-between"><span className="text-[#94A3B8]">Escenas estimadas</span><span className="font-semibold text-white">{mvConfirm?.scenes}</span></div>
                  <div className="flex justify-between"><span className="text-[#94A3B8]">Transición</span><span className="font-semibold text-white">{(MV_TRANSITIONS.find((t) => t.id === (mvTransition[mvConfirm?.p?.id] || "dissolve")) || {}).label}</span></div>
                  <div className="flex justify-between border-t border-white/10 pt-1.5"><span className="text-[#94A3B8]">Costo estimado</span><span className="inline-flex items-center gap-1.5 font-bold text-[#00F0FF]"><Coins className="w-4 h-4" />~{mvConfirm?.cost} créditos</span></div>
                </div>
                <p className="text-xs text-[#64748B]">Solo se cobra por las escenas entregadas. Si alguna falla, esos créditos se reembolsan automáticamente.</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="mv-confirm-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction data-testid="mv-confirm-ok" onClick={() => mvConfirm && runMusicVideo(mvConfirm.p, mvConfirm.full)}
              className="bg-[#FF9F2E] text-black hover:opacity-90">
              Confirmar y generar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!retryInfo} onOpenChange={(v) => !v && setRetryInfo(null)}>
        <AlertDialogContent data-testid="retry-cost-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Costo del reintento{retryInfo?.title ? ` · ${retryInfo.title}` : ""}</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                {retryInfo?.cost?.reuse_count > 0 && (
                  <p className="text-sm text-emerald-400" data-testid="retry-reuse-note">
                    ✓ {retryInfo.cost.reuse_count} escena(s) ya generada(s) se reutilizan GRATIS (no se vuelven a cobrar).
                  </p>
                )}
                {retryInfo?.cost?.invalid_voice?.length > 0 && (
                  <div className="rounded-xl border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-300" data-testid="retry-invalid-voice">
                    <p className="font-semibold flex items-center gap-1.5"><AlertTriangle className="w-4 h-4" /> Voz inválida — corrige antes de reintentar:</p>
                    <ul className="mt-1 list-disc pl-5">
                      {retryInfo.cost.invalid_voice.map((b) => (
                        <li key={b.scene}>Escena {b.scene}{b.speaker ? ` (${b.speaker})` : ""}: sube un audio real para este personaje.</li>
                      ))}
                    </ul>
                  </div>
                )}
                {retryInfo?.cost?.pending?.length > 0 ? (
                  <div className="rounded-xl border border-white/10 bg-white/5 p-3 space-y-1.5 text-sm">
                    <p className="text-[#94A3B8] mb-1">Se generará{retryInfo.cost.pending.length > 1 ? "n" : ""} {retryInfo.cost.pending.length} escena(s):</p>
                    {retryInfo.cost.pending.map((s) => (
                      <div key={s.scene} className="flex justify-between">
                        <span className="text-[#94A3B8]">Escena {s.scene} · {s.type} · {s.seconds}s{s.model ? <span className="text-[#64748B]"> · {s.model}</span> : null}</span>
                        <span className="font-semibold text-white">{s.credits} créditos</span>
                      </div>
                    ))}
                    <div className="flex justify-between border-t border-white/10 pt-1.5">
                      <span className="text-[#94A3B8]">Total a cobrar</span>
                      <span className="inline-flex items-center gap-1.5 font-bold text-[#00F0FF]" data-testid="retry-total-credits">
                        <Coins className="w-4 h-4" />{retryInfo.cost.admin ? "0 créditos (admin)" : `${retryInfo.cost.charge_credits ?? retryInfo.cost.total_credits} créditos`}
                      </span>
                    </div>
                    {!retryInfo.cost.admin && retryInfo.cost.balance != null && (
                      <div className={`flex justify-between text-xs ${retryInfo.cost.balance < retryInfo.cost.charge_credits ? "text-red-300" : "text-[#64748B]"}`} data-testid="retry-balance">
                        <span>Tu saldo</span>
                        <span>{retryInfo.cost.balance} créditos{retryInfo.cost.balance < retryInfo.cost.charge_credits ? " — insuficiente" : ""}</span>
                      </div>
                    )}
                    {!retryInfo.cost.admin && retryInfo.cost.balance < retryInfo.cost.charge_credits && (
                      <a href="/precios" data-testid="retry-recharge-link" className="block text-sm font-semibold text-red-300 underline">Recargar créditos</a>
                    )}
                    <p className="text-[11px] text-[#64748B]">Si alguna escena vuelve a fallar, sus créditos se te devuelven solos.</p>
                    {retryInfo?.cost?.byok && (
                      <div className="flex justify-between text-xs text-[#64748B]">
                        <span>Costo real estimado en tu cuenta de Fal</span>
                        <span>≈ ${retryInfo.cost.total_usd}</span>
                      </div>
                    )}
                    {retryInfo?.cost?.pending?.some((s) => s.type === "diálogo") && (
                      <p className="text-[11px] text-[#64748B] pt-0.5">Los diálogos facturan por la duración hablada real; el costo final en Fal puede variar un poco.</p>
                    )}
                  </div>
                ) : (
                  retryInfo?.cost?.invalid_voice?.length === 0 && (
                    <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-3 text-sm text-emerald-300" data-testid="retry-assemble-only">
                      <p className="font-semibold">Todas las escenas ya están generadas y pagadas.</p>
                      <p className="mt-0.5 text-emerald-200/80">Se reensamblará el video final <span className="font-semibold">GRATIS</span> (no se cobra nada ni se genera nada nuevo).</p>
                    </div>
                  )
                )}
                <p className="text-xs text-[#64748B]">Solo se cobra por las escenas que se generen. Nada se ejecuta hasta que confirmes.</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="retry-cost-cancel">Cancelar</AlertDialogCancel>
            {retryInfo?.cost?.pending?.length > 0 && retryInfo?.cost?.reuse_count > 0 && (
              <button data-testid="retry-assemble-only-btn" onClick={assembleOnly} disabled={retrying}
                className="px-4 py-2 rounded-md border border-emerald-500/40 text-emerald-300 text-sm font-semibold hover:bg-emerald-500/10 disabled:opacity-50">
                Unir solo las {retryInfo.cost.reuse_count} listas (gratis)
              </button>
            )}
            <AlertDialogAction data-testid="retry-cost-confirm" disabled={retrying || !retryInfo?.cost?.can_retry
                || (!retryInfo?.cost?.admin && (retryInfo?.cost?.balance ?? 0) < (retryInfo?.cost?.charge_credits || 0))}
              onClick={(e) => { e.preventDefault(); confirmRetry(); }}
              className="bg-[#FF9F2E] text-black hover:opacity-90 disabled:opacity-40">
              {retrying ? "Procesando…"
                : (retryInfo?.cost?.pending?.length
                    ? (retryInfo.cost.charge_credits ? `Confirmar y cobrar (${retryInfo.cost.charge_credits} cr)` : "Confirmar y generar")
                    : "Ensamblar video (gratis)")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {trailerFor && <TrailerDialog project={trailerFor} onClose={() => setTrailerFor(null)} onStarted={reload} />}
    </div>
  );
}
